import { ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, RouterEvent, Event } from '@angular/router';
import { UserService } from './services/user.service';
import { BackgroundProcessService } from './services/background-process.service';
import { FEATURE_FLAGS, FeatureFlagKey, PostHogService } from './services/posthog.service';
import { Storage } from '@ionic/storage-angular';
import { StylistService } from './services/stylist.service';
import { SaleService } from './services/sale.service';
import { ENV_CONFIG } from '../bin/env.config';
declare let gtag: Function;
import {
  PopoverController,
  NavController,
  MenuController,
  ModalController,
  ToastController,
  Platform,
} from '@ionic/angular';
import { NavComponent } from './components/nav/nav.component';
import { HelpPage } from './help/help.page';
import { ImpersonationBannerComponent } from './components/impersonation-banner/impersonation-banner.component';
import * as Sentry from '@sentry/angular';
import { SENTRY_REPLAY_PRIVACY_OPTIONS } from './sentry-replay-options';
import { PushNotifications } from '@capacitor/push-notifications';
import { StatusBar, Style } from '@capacitor/status-bar';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { presentLiveUpdateToast } from './utilities/live-update-toast';
import { Stylist } from './models/stylist/stylist.model';
import { SENTRY_PII_SCRUB_OPTIONS, scrubReplayEventUrls } from './sentry-pii-scrub';

/** Sub-entry of an expandable side-menu item (the "More ..." / Marketing lists). */
interface MenuChild {
  title: string;
  url: string;
  icon?: string;
  /** Gate this entry behind a PostHog flag; see syncFlaggedMenuItems(). */
  flag?: FeatureFlagKey;
  /**
   * Second, non-flag gate on this entry, resolved alongside `flag` in
   * syncFlaggedMenuItems(). `flag` answers "is this rolled out here"; this
   * answers "may THIS user use it" — a per-stylist permission, which PostHog
   * targets salons rather than staff and so cannot express.
   *
   * Declared here rather than handled by another `p.title == '...'` comparison
   * in the template: the expanded and collapsed menus already disagree about
   * which items they gate that way (Marketing is admin-only in both, Settings
   * and Reports only in the expanded one), and a permission gate that applies
   * to one layout and not the other is exactly the leak this feature can't
   * afford. Folding it into `hidden` means both layouts read one field.
   *
   * MUST fail closed: an unresolved user returns false, matching
   * ConnectAccountsAccessGuard, so the entry is hidden until the capability is
   * positively established.
   */
  permitted?: () => boolean;
  /**
   * Removed from the menu entirely. Derived from `flag` and `permitted`,
   * never hand-set.
   */
  hidden?: boolean;
  /**
   * Count shown as a badge on this entry, or absent for no badge. Derived from
   * a service monitor and written from one place only (see ngOnInit), so the
   * expanded and collapsed menus can never disagree about it — the same
   * discipline `hidden` gets from syncFlaggedMenuItems().
   */
  badgeCount?: number;
}

/**
 * Side-menu entry (pages_1 / pages_2 arrays). Inherits `flag`, `permitted`
 * and `hidden` from MenuChild so a top-level entry and a sub-entry are gated
 * by exactly the same two predicates.
 */
interface MenuPage extends MenuChild {
  icon: string;
  /** Renders an empty spacer row instead of a nav item. */
  disabled?: boolean;
  expandable?: boolean;
  children?: MenuChild[];
  /**
   * `children` minus flag-hidden entries, recomputed by syncFlaggedMenuItems().
   * Precomputed rather than filtered in the template: the accordion's *ngFor
   * binding is re-evaluated on every change-detection pass, so a filter() call
   * there would allocate and re-walk a fresh array each pass (no re-render —
   * the differ compares item identities) for a list that changes a handful of
   * times per session. Also keeps `hidden` and this list to one write site.
   */
  visibleChildren?: MenuChild[];
  /** Show "create sale" action in the menu (triggers createSale on the Sales page). */
  menuCreateAction?: boolean;
}

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  pages_1: MenuPage[] = [
    {
      title: 'Bookings',
      url: '/bookings',
      icon: 'calendar',
    },
    {
      title: 'Clients',
      url: '/clients',
      icon: 'person',
    },
  ];
  pages_2: MenuPage[] = [
    {
      title: 'Sales',
      url: '/sales',
      icon: 'card',
      menuCreateAction: true,
    },
    {
      title: 'Register Closures',
      url: '/register-closures',
      icon: 'cash',
    },
    {
      title: 'Products',
      url: '/products',
      icon: 'pricetags',
      expandable: true,
      children: [
        { title: 'Products', url: '/products' },
        { title: 'Categories', url: '/categories' },
        { title: 'Brands', url: '/brands' },
        { title: 'Suppliers', url: '/suppliers' },
      ],
    },
    {
      title: 'Inventory',
      url: '/inventory',
      icon: 'cube',
      expandable: true,
      children: [
        { title: 'Purchase Orders', url: '/inventory' },
        { title: 'Inventory History', url: '/inventory-history' },
        { title: 'Inventory Count', url: '/inventory-count' },
      ],
    },
    {
      title: 'Marketing',
      url: '/marketing',
      icon: 'send',
      expandable: true,
      children: [
        {title: 'Email', url: '/marketing'},
        // Replaces the hardcoded comment-out from 81996b60: bulk text can now
        // be switched on per salon from PostHog instead of needing a release.
        {title: 'Text', url: '/text-marketing', flag: FEATURE_FLAGS.bulkText},
      ]
    },
    {
      title: 'Reports',
      url: '/reports',
      icon: 'pie-chart',
      disabled: false,
    },
    {
      title: '',
      url: '',
      icon: '',
      disabled: true,
    },
    {
      title: 'Connect with co-workers',
      url: '/connections',
      icon: 'link',
      flag: FEATURE_FLAGS.connectableAccounts,
      // Mirrors ConnectAccountsAccessGuard exactly, and for the same reason:
      // a stylist without the permission cannot reach Connections, so they
      // must not be offered it either. `getCanConnectAccountsEffective()` is
      // the one thing to read — it already folds in `administrator`, so an
      // admin sees the entry without the column being set.
      permitted: () => this.canConnectAccounts(),
    },
    {
      title: 'More ...',
      url: '',
      icon: 'ellipsis-horizontal',
      expandable: true,
      children: [
        { title: 'Accept Online Bookings', url: '/online-bookings', icon: 'desktop' },
        { title: 'Import Contacts', url: '/import-contacts', icon: 'receipt' },
        { title: 'Subscribe to Calendar', url: '/calendar-sync', icon: 'today' },
      ],
    },
    {
      title: '',
      url: '',
      icon: '',
      disabled: true,
    },
    {
      title: 'Settings',
      url: '/settings',
      icon: 'settings',
      disabled: false,
    },
    /*{
      title: 'Help',
      url: '/help',
      icon: 'help',
    },  */
  ];

  user = undefined;

  profileImg = '';

  selectedPath = '';

  bookingWaitListCount = 0;

  bookingRequestCount = 0;

  isLoggedIn = false;

  public collapsed: boolean = false;
  public appVersion: string;
  public loginLastRecorded: number = Date.now();
  public allowSettings: boolean = false;
  public allowReports: boolean = false;
  public isNativeApp: boolean = false;
  private onForeground?: () => void;
  private detachFeatureFlagListener?: () => void;
  private darkModeQuery?: MediaQueryList;
  private onColorSchemeChange?: () => void;
  private updateCheckInFlight = false;
  private lastUpdateCheck = 0;
  private launchCheckTimer?: ReturnType<typeof setTimeout>;
  // Throttle foreground OTA checks (mirrors Capawesome's `background` strategy).
  private static readonly UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;
  // A failed check retries sooner than the full interval, but not on the very
  // next foreground — see the catch in checkForLiveUpdate().
  private static readonly UPDATE_CHECK_RETRY_BACKOFF_MS = 60 * 1000;
  // The one launch-time check waits this long so the Capawesome round-trip
  // never competes with login and the calendar's first data load on cellular —
  // the exact contention the foreground throttle exists to prevent.
  private static readonly LAUNCH_UPDATE_CHECK_DELAY_MS = 10 * 1000;
  /** Pages shown without the logged-in shell. `/password-reset-password` carries a `/:token` segment. */
  private static readonly PUBLIC_URLS = ['/login', '/register', '/password-reset', '/password-reset-password'];

  /**
   * GA4 `surface` dimension for every hit from this app: `app_native` inside a
   * Capacitor shell, `app_web` in the browser. Detected with the same guard as
   * index.html / sign-up.service so all three agree. Shared with the marketing
   * site's GA4 property, this is what lets us split app traffic from web.
   */
  private readonly surface: 'app_web' | 'app_native' =
    typeof window !== 'undefined' &&
    ((window as any).Capacitor || window.location.protocol === 'capacitor:')
      ? 'app_native'
      : 'app_web';

  constructor(
    public router: Router,
    public userService: UserService,
    private backgroundProcessService: BackgroundProcessService,
    private storage: Storage,
    private popoverCtrl: PopoverController,
    private stylistService: StylistService,
    private platform: Platform,
    private navCtrl: NavController,
    private menuCtrl: MenuController,
    private modalCtrl: ModalController,
    private toastCtrl: ToastController,
    private changeDetectorRef: ChangeDetectorRef,
    private saleService: SaleService,
    private postHogService: PostHogService,
  ) {
    this.appVersion = ENV_CONFIG.appVersion;
    this.isNativeApp = this.platform.is('hybrid');

    // Set `surface` once at bootstrap so every GA4 hit (including auto pageviews)
    // inherits it; it's also passed on the config call below belt-and-suspenders.
    if (typeof gtag !== 'undefined') {
      gtag('set', { surface: this.surface });
    }

    this.router.events.subscribe((event: Event) => {
      if ((event as RouterEvent).url) {
        this.selectedPath = (event as RouterEvent).url;
      }
      if (event instanceof NavigationEnd) {
        // Route the GA4 pageview by surface. The acquisition property must only
        // ever receive the marketing site + the signup funnel so that
        // marketing-source→trial attribution stays joinable; authenticated
        // product usage goes to a separate product property when configured,
        // otherwise nowhere (PostHog owns product analytics).
        const ACQUISITION_GA4_ID = 'G-MFKK9R8PZ9';
        const PRODUCT_GA4_ID = ENV_CONFIG.googleAnalyticsProductMeasurementID;
        const url = event.urlAfterRedirects;
        // The signup funnel lives under /register — treat it as acquisition.
        const isAcquisition = url.startsWith('/register');
        if (typeof gtag !== 'undefined') {
          if (isAcquisition) {
            gtag('config', ACQUISITION_GA4_ID, {
              page_path: 'app' + url,
              surface: this.surface,
            });
          } else if (PRODUCT_GA4_ID) {
            gtag('config', PRODUCT_GA4_ID, {
              page_path: 'app' + url,
              surface: this.surface,
            });
          }
          // else: authenticated product route with no product property
          // configured — skip GA4 entirely (PostHog still fires below).
        }
        // SPA pageview for PostHog — auto-pageview is off in PostHogService.init()
        // so marketing-site anonymous activity bridges to authenticated users
        // via a consistent manual pageview stream.
        this.postHogService.capturePageview(window.location.href, {
          page_path: event.urlAfterRedirects,
        });
      }
    });

    // Resolve gated entries up front so they start hidden: flags load
    // asynchronously, and onFeatureFlags never fires at all if PostHog failed
    // to initialize, so this eager pass is what keeps the default fail-closed.
    this.syncFlaggedMenuItems();
    // Then re-resolve on every flags (re)load: initial fetch, after identify()
    // on login (per-salon targeting), and after reset() on logout.
    this.detachFeatureFlagListener = this.postHogService.onFeatureFlags(() => {
      this.syncFlaggedMenuItems();
      this.changeDetectorRef.markForCheck();
    });

    // Every logout must drop `body.impersonating`, not only AppComponent.logout():
    // BackgroundProcessService (401 / inactive account), LoginPage and
    // ExportDataPage call userService.logout() directly, and the global
    // `body.impersonating ion-split-pane` rule would otherwise keep the login
    // page's split pane offset by the banner height with no banner above it.
    //
    // Those callers bypass the shell too. Without this, a background logout
    // clears the user and token but leaves the menu and current page up over
    // them, so the menu reads a missing user and the calendar never loads.
    this.userService.getLogOutMonitor().subscribe((loggedOut) => {
      this.updateImpersonationBodyClass();
      if (!loggedOut) {
        return;
      }
      this.isLoggedIn = false;
      if (!this.isPublicPage) {
        void this.router.navigate(['/login']);
      }
    });

    this.allowSettings = this.pages_2.find((x) => x.title == 'Settings')?.disabled ?? false;
    // this.allowReports = this.pages_2.find(x=>x.title == 'Reports').disabled;

    /*if((this.stylists.canViewReports === 0) || (this.stylists.settingsControl === 0)){
      this.allowSettings = false;
      this.allowReports = false;
      //this.pages_2.find(x=>x.title == 'Settings').disabled = true;
      //this.pages_2.find(x=>x.title == 'Reports').disabled = true;
    }*/
  }

  /** True on a page shown without the logged-in shell; decides which split pane renders. */
  public get isPublicPage(): boolean {
    const path = this.router.url.split(/[?#]/)[0];
    return AppComponent.PUBLIC_URLS.some((url) => path === url || path.startsWith(url + '/'));
  }

  /** Navigate to Sales and request the create-sale flow (used by menu button). */
  requestCreateSale($event: MouseEvent): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.saleService.setPendingCreateSale();
    this.router.navigate(['/sales']);
    this.menuCtrl.close();
  }

  async ngOnInit() {
    // Consume an admin impersonation token if one arrived in the URL. Must run
    // synchronously, before the `await` below: the router's initial (lazy)
    // navigation can otherwise mount LoginPage first and strand the admin on
    // the login form, and the token must be scrubbed from the URL as early as
    // possible so it is not observable for longer than necessary.
    this.consumeImpersonationToken();

    this.isLoggedIn = this.userService.isLoggedIn();
    this.userService.profileImageMonitor$.subscribe((data: string) => {
      this.profileImg = ENV_CONFIG.STAFF_THUMBNAIL_PHOTO_ROOT + '/' + data;
      // Handle data change in AppComponent
    });
    await this.storage.create();
    this.storage.set('collapsed', false);

    if (this.platform.is('capacitor')) {
      void LiveUpdate.ready();

      // Check for a new OTA bundle whenever the app returns to the foreground.
      // Registered here in ngOnInit (which DOES fire on the root component) — the
      // previous listener lived in ionViewWillEnter, which Ionic never calls on
      // the root AppComponent, so resume-based update checks never ran. Uses the
      // web `visibilitychange` event (no native plugin) so this logic itself
      // ships via OTA. NOTE: this picks up new bundles on the *current* channel;
      // switching release tracks still needs the salon to reload
      // (UserService.syncLiveUpdateChannel runs on setSalon).
      this.onForeground = () => {
        if (document.visibilityState === 'visible') {
          // Refresh the view on every resume; the update check itself is
          // throttled inside checkForLiveUpdate().
          this.changeDetectorRef.markForCheck();
          void this.checkForLiveUpdate();
          // The webview suspends setInterval while backgrounded, so without
          // this the 30-minute cycle never fires on a phone and the badge can
          // stay stale for the whole session. The refresh no-ops when logged
          // out or when the flag is off.
          this.backgroundProcessService.refreshPendingConnectionRequests();
        }
      };
      document.addEventListener('visibilitychange', this.onForeground);

      // Also check once per cold start. `visibilitychange` never fires on
      // launch — the document is born visible — so a force-quit + relaunch
      // otherwise NEVER checks for an update, and "close the app and open it
      // again" is exactly what we tell users to do to pick one up. Deferred by
      // LAUNCH_UPDATE_CHECK_DELAY_MS (see the constant); the shared throttle
      // in checkForLiveUpdate() then absorbs the first foreground check that
      // would otherwise duplicate this one.
      this.launchCheckTimer = setTimeout(() => {
        void this.checkForLiveUpdate('launch_check');
      }, AppComponent.LAUNCH_UPDATE_CHECK_DELAY_MS);

      if (this.platform.is('android')) {
        // targetSdk 35 enforces edge-to-edge: the status bar is transparent
        // over the app background, so icon color must track light/dark mode or
        // the icons vanish (white-on-white). The native theme (values/ +
        // values-night/) sets the first frame; this listener keeps it correct
        // when the system theme flips while the app is running, because uiMode
        // is in the activity's configChanges so the theme is never re-applied.
        // Style.Default can't be used here: Android freezes it at the launch
        // appearance (captured once at plugin construction), so derive the
        // style from the media query — Style.Dark = light icons on a dark
        // background, Style.Light = dark icons on a light background.
        this.darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
        this.onColorSchemeChange = () => {
          void StatusBar.setStyle({
            style: this.darkModeQuery?.matches ? Style.Dark : Style.Light,
          });
        };
        this.onColorSchemeChange();
        this.darkModeQuery.addEventListener('change', this.onColorSchemeChange);
      }
    }
    // The permission can change mid-session. refreshSession() calls setUser()
    // and nexts this monitor, but the menu only re-resolved on LOGIN — so an
    // administrator granting or revoking connect-accounts left the entry in
    // its old state until the app was relaunched. The guard still decided
    // correctly, which made the disagreement the visible bug: an entry
    // offering a page that bounces, or hidden on one that would open.
    //
    // Seeded with undefined, so ignore that first emission.
    this.userService.getSessionMonitor().subscribe((auth) => {
      if (!auth) {
        return;
      }
      this.user = this.userService.getUser();
      this.syncFlaggedMenuItems();
      this.changeDetectorRef.markForCheck();
    });

    this.userService.getLogInMonitor().subscribe((login: boolean) => {
      if (login) {
        this.isLoggedIn = login;
        if (this.userService.getUser()) {
          this.user = this.userService.getUser();
          //canViewReports == 1
          //settingsControl ==1
          if (this.userService.getUser().getFullThumbnailUrl()) {
            this.profileImg = this.userService.getUser().getFullThumbnailUrl();
          }
        }
        // Permission-gated entries (`permitted`) resolve against the signed-in
        // stylist, which did not exist at construction time — so re-resolve
        // now. The PostHog listener alone is not enough to rely on: it fires
        // after identify(), but never at all if PostHog failed to initialise,
        // and that must not be what decides whether a permitted stylist can
        // see their own menu entry.
        this.syncFlaggedMenuItems();
        this.changeDetectorRef.markForCheck();

        this.updateImpersonationBodyClass();
      }
    });

    this.backgroundProcessService.bookingRequestMonitor$.subscribe((bookingRequestCount: number) => {
      this.bookingRequestCount = bookingRequestCount;
    });

    this.backgroundProcessService.bookingWaitListMonitor$.subscribe((bookingWaitListCount: number) => {
      this.bookingWaitListCount = bookingWaitListCount;
    });

    // Connect with co-workers renders through the generic pages_2 loop rather
    // than a hand-written item like Requests and Waitlist, so the count lives
    // on the menu entry itself and both layouts read the same field.
    this.backgroundProcessService.pendingConnectionRequestMonitor$.subscribe((count: number) => {
      const connectionsPage = this.pages_2.find((page) => page.url === '/connections');
      if (connectionsPage) {
        connectionsPage.badgeCount = count;
      }
    });

    // Request permission to use push notifications
    // iOS will prompt user and return if they granted permission or not
    // Android will just grant without prompting
    if (this.platform.is('capacitor')) {
      PushNotifications.requestPermissions().then((result) => {
        if (result.receive === 'granted') {
          // Register with Apple / Google to receive push via APNS/FCM
          PushNotifications.register();
        } else {
          // Show some error
        }
      });
    }

    // activate Sentry
    Sentry.init({
      dsn: ENV_CONFIG.SENTRY_DSN,
      environment: ENV_CONFIG.SENTRY_ENVIRONMENT,
      // Redacts client search terms, emails, phone numbers and reset tokens from
      // URLs in breadcrumbs, spans, transactions and error messages.
      ...SENTRY_PII_SCRUB_OPTIONS,
      // release: ENV_CONFIG.appVersion,
      integrations: [
        // Registers and configures the Tracing integration,
        // which automatically instruments your application to monitor its
        // performance, including custom Angular routing instrumentation
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(SENTRY_REPLAY_PRIVACY_OPTIONS),
      ],

      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 0.2,

      // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/api.salonmonster\.com\/api/,
        /^https:\/\/api2.salonmonster\.com\/api/,
        /^https:\/\/api3.salonmonster\.com\/api/,
        /^https:\/\/api4.salonmonster\.com\/api/,
      ],

      // Capture Replay for 10% of all sessions (Sentry's production guidance),
      // plus for 100% of sessions with an error. 10% rather than errors-only so
      // a reported problem that raised no error still has a chance of a replay.
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    });
    Sentry.getGlobalScope().addEventProcessor(scrubReplayEventUrls);
  }

  public ngOnDestroy() {
    if (this.onForeground) {
      document.removeEventListener('visibilitychange', this.onForeground);
    }
    if (this.launchCheckTimer !== undefined) {
      clearTimeout(this.launchCheckTimer);
    }
    this.detachFeatureFlagListener?.();
    if (this.darkModeQuery && this.onColorSchemeChange) {
      this.darkModeQuery.removeEventListener('change', this.onColorSchemeChange);
    }
  }

  /**
   * Ask Capawesome whether a newer bundle exists on the device's current channel.
   * If so, it has already been downloaded and staged — Capawesome applies it on
   * the next app start (ready() is called on boot), so we never force a
   * disruptive mid-session reload. We surface a non-blocking toast with an opt-in
   * "Restart now" instead. Throttled to one network check per
   * UPDATE_CHECK_INTERVAL_MS and guarded against overlapping syncs.
   *
   * Both guards matter: `visibilitychange` fires on every foreground (and on
   * iOS during app-switcher/Control-Centre passes), so an unthrottled sync()
   * put a Capawesome Cloud round-trip on the critical path of every resume —
   * competing with the calendar's first data load on cellular. The previous
   * blocking confirm() also froze the WebView until it was dismissed.
   */
  private async checkForLiveUpdate(
    source: 'foreground_check' | 'launch_check' = 'foreground_check'
  ): Promise<void> {
    if (this.updateCheckInFlight) return;
    const now = Date.now();
    if (now - this.lastUpdateCheck < AppComponent.UPDATE_CHECK_INTERVAL_MS) return;
    this.lastUpdateCheck = now;
    this.updateCheckInFlight = true;
    try {
      const { nextBundleId } = await LiveUpdate.sync();
      if (nextBundleId) {
        await presentLiveUpdateToast(this.toastCtrl);
      }
    } catch (err) {
      // Never let a failed check block app usage, but do not clear the throttle
      // outright: sync() fails exactly when the network is bad, and zeroing it
      // put a Capawesome round-trip back on EVERY foreground in precisely that
      // case — the behaviour this throttle exists to remove. An hour offline
      // with 20 foregrounds meant ~20 attempts. Back off to a shorter floor so a
      // one-off blip still recovers quickly without reverting to per-resume.
      this.lastUpdateCheck =
        now -
        AppComponent.UPDATE_CHECK_INTERVAL_MS +
        AppComponent.UPDATE_CHECK_RETRY_BACKOFF_MS;
      // Was swallowed entirely — not even a console.error — so a fleet-wide OTA
      // delivery failure would be completely silent, on the mechanism we rely
      // on to reach devices we cannot otherwise fix.
      //
      // The PostHog event is what actually makes this visible. A Sentry
      // breadcrumb alone ships nothing: it is only attached to a later
      // captureException in the same session, and this catch swallows the
      // error so the global handler never sees it either. The breadcrumb stays
      // as context for any unrelated error reported afterwards.
      console.error('[OTA] update check failed', err);
      this.postHogService.capture('ota_update_check_failed', {
        source,
      });
      Sentry.addBreadcrumb({
        category: 'ota.sync',
        message: 'live update check failed',
        level: 'warning',
      });
    } finally {
      this.updateCheckInFlight = false;
    }
  }


  /**
   * Whether the signed-in stylist may take part in connectable accounts.
   *
   * Reads `getCanConnectAccountsEffective()` — the server's
   * `administrator OR canConnectAccounts` — which is also what
   * ConnectAccountsAccessGuard gates on, so the menu and the route can never
   * disagree about who the feature belongs to.
   *
   * Fails closed. No user, or a user object restored before the accessor
   * existed, answers false: the menu entry stays hidden rather than offering a
   * surface the guard will bounce.
   */
  private canConnectAccounts(): boolean {
    return Stylist.mayConnectAccounts(this.userService.getUser());
  }

  /**
   * Resolves every gated menu entry and refreshes each expandable page's
   * `visibleChildren`. The single place `hidden` is written, so all three
   * paths an entry can render through — the expanded accordion, the collapsed
   * rail and the collapsed-menu popover — always agree; a gated entry can't
   * leak via one while being hidden on the others.
   *
   * Two gates, ANDed, each failing closed on its own: `flag` (PostHog rollout,
   * per salon) and `permitted` (per-stylist capability). An entry carrying
   * both is shown only when both say yes, which is the menu-side mirror of the
   * FeatureFlagGuard + ConnectAccountsAccessGuard pair on the route.
   */
  private syncFlaggedMenuItems(): void {
    for (const page of [...this.pages_1, ...this.pages_2]) {
      for (const entry of [page, ...(page.children ?? [])]) {
        if (entry.flag || entry.permitted) {
          const rolledOut = entry.flag ? this.postHogService.isFeatureEnabled(entry.flag) : true;
          const allowed = entry.permitted ? entry.permitted() : true;
          entry.hidden = !(rolledOut && allowed);
        }
      }
      if (page.children) {
        page.visibleChildren = page.children.filter((child) => !child.hidden);
      }
    }
  }

  public async popover(ev: any, page: MenuPage) {
    const popover = await this.popoverCtrl.create({
      component: NavComponent,
      event: ev,
      componentProps: {
        options: page.visibleChildren ?? [],
      },
    });
    popover.present();
  }

  public collapseMenu() {
    this.collapsed = !this.collapsed;
    this.storage.set('collapsed', this.collapsed);
  }

  public logout() {
    this.isLoggedIn = false;
    // The logout-monitor subscription (constructor) drops `body.impersonating`.
    this.userService.logout();
  }

  public open(url: string) {
    if (url == 'https://salontastic.salonmonster.com') {
      window.location.href = url;
      return;
    }
    this.navCtrl.setDirection('root');
    this.router.navigateByUrl(url);
    this.menuCtrl.toggle();
  }

  public async onClickHelp() {
    // HelpPage is declared in HelpPageModule, which is lazy. Loading it here gives the
    // modal its template scope in a JIT build without pulling Help into the root bundle.
    await import('./help/help.module');
    const modal = await this.modalCtrl.create({
      component: HelpPage,
    });
    return await modal.present();
  }

  public onReload() {
    // This reloads the app's index.html page with a random query string to force it to reload from the server (not cache)
    var randomNumber = Math.floor(Math.random() * 1000000000); // Generates a random number
    var base = window.location.href.split('#')[0]; // Get the base URL before the hash
    var newUrl = base.replace(/\/$/, '') + '/index.html?update=' + randomNumber; // Append 'index.html' with the update parameter
    window.location.href = newUrl; // Redirect to the new URL
  }

  /** `body.impersonating ion-split-pane` (global.scss) makes room for the banner. */
  private updateImpersonationBodyClass() {
    document.body.classList.toggle('impersonating', this.userService.isImpersonating());
  }

  /**
   * Establish an impersonated session from a `token` URL parameter, if one is
   * present. Only the token travels in the URL; BackgroundProcessService loads
   * the real stylist and salon on the login emission. Returns true when an
   * impersonation was started.
   */
  public consumeImpersonationToken(): boolean {
    if (!this.userService.consumeImpersonationTokenFromUrl()) {
      return false;
    }
    this.updateImpersonationBodyClass();
    return true;
  }
}
